import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { type AssessmentResult, type CheckResult, type CategoryScore } from '../types/assessment';

// Helper for null-safe number formatting
function safeNum(value: number | null | undefined, fallback: number = 0): number {
  return typeof value === 'number' && !isNaN(value) ? value : fallback;
}

export function generatePDF(result: AssessmentResult): jsPDF {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Header
  doc.setFontSize(20);
  doc.setTextColor(0, 86, 179);
  doc.text('CrowdStrike Falcon Health Check Assessment', pageWidth / 2, 20, { align: 'center' });
  
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text('CONFIDENTIAL - Generated from memory, not stored on any server', pageWidth / 2, 28, { align: 'center' });
  
  // Metadata
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  let y = 40;
  
  const coverage = safeNum(result.coverage);
  const overallScore = safeNum(result.overallScore);
  
  const meta = [
    ['Customer:', result.customerName || 'N/A'],
    ['Assessor:', result.assessorName || 'N/A'],
    ['Assessment Date:', result.assessmentDate ? new Date(result.assessmentDate).toLocaleString() : 'N/A'],
    ['Period:', `${result.assessmentPeriodDays ?? 'N/A'} days`],
    ['Overall Score:', `${overallScore}/100`],
    ['Coverage:', `${coverage.toFixed(1)}%`],
  ];
  
  meta.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(label, 14, y);
    doc.setFont('helvetica', 'normal');
    doc.text(String(value), 50, y);
    y += 6;
  });
  
  // Executive Summary
  y += 4;
  doc.setFontSize(14);
  doc.setTextColor(0, 86, 179);
  doc.text('Executive Summary', 14, y);
  y += 8;
  
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  const totalChecks = safeNum(result.summary?.totalChecks);
  const totalCats = result.categoryScores?.length ?? 0;
  const summaryText = [
    `This assessment evaluated ${totalChecks} checks across ${totalCats} categories.`,
    `Results: ${safeNum(result.summary?.passCount)} passed, ${safeNum(result.summary?.warningCount)} warnings, ${safeNum(result.summary?.failCount)} failures, ${safeNum(result.summary?.notAssessedCount)} not assessed.`,
    safeNum(result.summary?.failCount) > 0 
      ? `Critical Action Required: ${safeNum(result.summary?.failCount)} failed checks require immediate attention.`
      : 'No critical failures detected. Continue maintaining current posture.',
  ];
  
  summaryText.forEach(text => {
    const lines = doc.splitTextToSize(text, pageWidth - 28);
    doc.text(lines, 14, y);
    y += lines.length * 5 + 2;
  });
  
  // Category Scores Table
  y += 4;
  if (y > 240) {
    doc.addPage();
    y = 20;
  }
  
  doc.setFontSize(14);
  doc.setTextColor(0, 86, 179);
  doc.text('Category Scores', 14, y);
  y += 6;
  
  autoTable(doc, {
    startY: y,
    head: [['Category', 'Checks', 'Pass', 'Warning', 'Fail', 'Score']],
    body: (result.categoryScores || []).map((cat: CategoryScore) => [
      cat.category,
      safeNum(cat.totalChecks),
      safeNum(cat.passCount),
      safeNum(cat.warningCount),
      safeNum(cat.failCount),
      `${safeNum(cat.score)}/100`,
    ]),
    theme: 'grid',
    headStyles: { fillColor: [0, 86, 179], textColor: 255 },
    styles: { fontSize: 9 },
  });
  
  // Detailed Findings
  const findings = (result.checks || []).filter((c: CheckResult) => c.status !== 'PASS');
  if (findings.length > 0) {
    doc.addPage();
    doc.setFontSize(14);
    doc.setTextColor(0, 86, 179);
    doc.text('Detailed Findings and Recommendations', 14, 20);
    
    autoTable(doc, {
      startY: 26,
      head: [['Check', 'Category', 'Status', 'Finding', 'Recommendation']],
      body: findings.map((check: CheckResult) => [
        `${check.checkId}: ${check.checkName}`,
        check.category,
        check.status,
        check.finding || '',
        check.recommendation || '',
      ]),
      theme: 'grid',
      headStyles: { fillColor: [0, 86, 179], textColor: 255 },
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 35 },
        2: { cellWidth: 20 },
        3: { cellWidth: 'auto' },
        4: { cellWidth: 'auto' },
      },
    });
  }
  
  // Limitations
  if (result.limitations && result.limitations.length > 0) {
    const finalY = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY || 200;
    if (finalY > 240) {
      doc.addPage();
    }
    
    doc.setFontSize(12);
    doc.setTextColor(0, 86, 179);
    doc.text('Assessment Limitations', 14, finalY + 10);
    
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    result.limitations.forEach((limitation: string, index: number) => {
      const lines = doc.splitTextToSize(`${index + 1}. ${limitation}`, pageWidth - 28);
      doc.text(lines, 14, finalY + 18 + (index * 10));
    });
  }
  
  // Footer on all pages
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Page ${i} of ${pageCount} | CrowdStrike Health Assessment | Not stored on any server`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }
  
  return doc;
}

export function downloadPDF(result: AssessmentResult): void {
  const doc = generatePDF(result);
  const safeName = (result.customerName || 'Assessment').replace(/[^a-z0-9]/gi, '_');
  const filename = `CrowdStrike_Assessment_${safeName}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(filename);
}
